import axios from "axios"
import { useQuery } from "@tanstack/react-query"
import { type GachaboxStatus, type IGachabox } from "@repo/loomlock/model-types"
import { useCallback, useMemo } from "react"
import { useVaults } from "@abraxas/ui/web3"
import { useIndexerQuery } from "@/shared/indexer/useIndexerQuery"
import { ABIGachaboxMinterV2, config } from "@repo/loomlock/client"
import { useReadContract } from "wagmi"
import type { Entities } from "@/shared/indexer/entititiesTypes"
import { captureException } from "@sentry/nextjs"

export const polyfillGachaboxV2 = (gachabox: IGachabox): IGachabox => {
  if (gachabox.gachaboxId !== 1) {
    return gachabox
  }

  return {
    ...gachabox,
    tokens: gachabox.tokens || [],
    paymentMethods: gachabox.paymentMethods || [
      {
        tokenAddress: config[gachabox.chainId]?.contracts.loomlockCoupon! as `0x${string}`,
        tokenType: 1,
        amount: "1",
        tokenId: 1,
        burnOnFulfill: true,
      },
    ],
  }
}

// Database queries
export const useGachaboxDB = (slug: string, admin: boolean = false) => {
  return useQuery<IGachabox>({
    queryKey: ["gachabox", admin, slug],
    queryFn: async () => {
      const response = await axios.get(
        `/api/${admin ? "wadmin/" : ""}gachaboxes/${slug}`,
      )
      return polyfillGachaboxV2(response.data)
    },
    enabled: Boolean(slug),
  })
}

export const useGachaboxIndexer = ({
  id,
  chainId,
}: {
  id: number | undefined
  chainId: number | undefined
}) => {
  return useIndexerQuery({
    commandName: "gachaboxes",
    variables: {
      gachaboxId: id,
      chainId,
    },
    enabled: Boolean(id !== undefined && chainId !== undefined),
    select: (data) => {
      const gachabox = data.data.Gachabox?.[0]
      if (!gachabox) {
        captureException(new Error("Gachabox not found"), {
          extra: {
            gachaboxId: id,
            chainId,
          },
        })
        return {
          total: 0,
          mintedCount: 0,
          pendingCount: 0,
          totalRemaining: 0,
          participantsCount: 0,
          paused: false,
        }
      }
      return {
        total: gachabox.total,
        mintedCount: gachabox.mintedCount,
        pendingCount: gachabox.pendingCount,
        totalRemaining: gachabox.total - gachabox.mintedCount,
        participantsCount: gachabox.participantsCount,
        paused: gachabox.paused,
      }
    },
  })
}

export const useGachabox = (slug: string, admin = false) => {
  const dbQuery = useGachaboxDB(slug, admin)

  const indexerQuery = useGachaboxIndexer({
    id: dbQuery.data?.gachaboxId,
    chainId: dbQuery.data?.chainId,
  })

  const { data: status } = useReadContract({
    abi: ABIGachaboxMinterV2,
    chainId: dbQuery.data?.chainId,
    address: dbQuery.data?.contractAddress as `0x${string}`,
    args: [dbQuery.data?.gachaboxId as any],
    functionName: "getGachaboxStatus",
    query: {
      enabled: Boolean(dbQuery.data?.gachaboxId),
    },
  })

  const data = useMemo<
    null | (IGachabox & Entities["Gachabox"] & { status: GachaboxStatus })
  >(
    () =>
      !dbQuery.data
        ? null
        : {
          ...dbQuery.data,
          ...(indexerQuery.data as any),
          status: status as any,
        },
    [dbQuery.data, indexerQuery.data, status],
  )

  const { refetch: refetchDb } = dbQuery
  const { refetch: refetchIndexer } = indexerQuery

  return {
    data,
    isLoading: dbQuery.isLoading || indexerQuery.isLoading,
    error: dbQuery.error || indexerQuery.error,
    refetch: useCallback(() => {
      refetchDb()
      refetchIndexer()
    }, [refetchDb, refetchIndexer]),
    dbQuery,
    indexerQuery,
  }
}

export const fetchPromotedGachabox = async () => {
  const response = await axios.get("/api/gachaboxes/promoted")
  return polyfillGachaboxV2(response.data)
}

export const usePromotedGachaboxDB = () => {
  return useQuery<IGachabox>({
    queryKey: ["gachabox", "promoted"],
    queryFn: fetchPromotedGachabox,
  })
}

export const usePromotedGachabox = () => {
  const dbQuery = usePromotedGachaboxDB()

  const indexerQuery = useGachaboxIndexer({
    id: dbQuery.data?.gachaboxId,
    chainId: dbQuery.data?.chainId,
  })

  const data = useMemo(
    () =>
      !dbQuery.data
        ? null
        : {
          ...dbQuery.data,
          ...(indexerQuery.data as any),
        },
    [dbQuery.data, indexerQuery.data],
  )

  const { refetch: refetchDb } = dbQuery
  const { refetch: refetchIndexer } = indexerQuery

  return {
    data,
    isLoading: dbQuery.isLoading || indexerQuery.isLoading,
    error: dbQuery.error || indexerQuery.error,
    refetch: useCallback(() => {
      refetchDb()
      refetchIndexer()
    }, [refetchDb, refetchIndexer]),
    dbQuery,
    indexerQuery,
  }
}

export const useGachaboxMints = (
  gachaboxId: number | undefined,
  participantAddress?: `0x${string}`,
) => {
  const { effectiveAddress: connectedAddress } = useVaults()
  const filterAddress = participantAddress ?? connectedAddress

  const allMintsQuery = useIndexerQuery({
    commandName: "gachaboxMints",
    variables: {
      gachaboxId,
    },
    enabled: Boolean(gachaboxId !== undefined),
    select: (data) => data.data.GachaboxMint || [],
  })

  const myMintsQuery = useIndexerQuery({
    commandName: "gachaboxMints",
    variables: {
      gachaboxId,
      participant: filterAddress,
      // needed to prevent key collision
      some: "key",
    },
    enabled: Boolean(gachaboxId !== undefined && filterAddress),
    select: (data) => data.data.GachaboxMint || [],
  })

  const myMints = useMemo(
    () => (myMintsQuery.data ?? []) as Entities["GachaboxMint"][],
    [myMintsQuery.data],
  )

  const winnings = useMemo(() => {
    const allWinnings: string[] = []
    if (allMintsQuery.data) {
      allMintsQuery.data.forEach((mint: any) => {
        if (mint.tokenId !== null && mint.tokenId !== undefined) {
          allWinnings.push(String(mint.tokenId))
        }
      })
    }
    return allWinnings
  }, [allMintsQuery.data])

  const myWinnings = useMemo(() => {
    const myWins: string[] = []
    for (const mint of myMints) {
      if (mint.tokenId !== null && mint.tokenId !== undefined) {
        myWins.push(String(mint.tokenId))
        console.log("mint", myWins)
      }
    }
    console.log("myWins", myWins)
    return myWins
  }, [myMints])

  const { refetch: refetchMyMints } = myMintsQuery
  const { refetch: refetchAllMints } = allMintsQuery
  return {
    winnings,
    myWinnings,
    myMints,
    isLoading: allMintsQuery.isLoading || myMintsQuery.isLoading,
    error: allMintsQuery.error || myMintsQuery.error,
    pollForChange: myMintsQuery.pollForChange,
    refetch: useCallback(() => {
      refetchAllMints()
      refetchMyMints()
    }, [refetchAllMints, refetchMyMints]),
  }
}
